import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Heart, PenLine, MapPin, Clock, ChevronLeft, ChevronRight, Users, UserPlus, UserCheck, Bookmark, BookmarkCheck, X, ExternalLink, Volume2, Image as ImageIcon } from "lucide-react";
import EchoeDetailModal from "@/components/EchoeDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Landmarks", "Hidden Gems", "Vanished Voices", "Wild Echoes", "City Pulses"];
const CATEGORY_COLORS = {
  "Landmarks": "#2D6A4F",
  "Hidden Gems": "#B85C38",
  "Vanished Voices": "#5C4B8A",
  "Wild Echoes": "#1A5276",
  "City Pulses": "#8B5E3C",
};
const CATEGORY_DESCRIPTIONS = {
  "Landmarks": "Architectural Giants — The world-famous \"must-sees\" and engineering feats.",
  "Hidden Gems": "The Secrets — Mysterious or tucked-away details that most people walk past.",
  "Vanished Voices": "Lost Places & Events — Historic events or structures no longer physically there.",
  "Wild Echoes": "Parks & Natural Landscapes — Parks, waterfronts, geology, and wildlife.",
  "City Pulses": "Living Culture — The cultural movements, traditions, and community identities that make each neighborhood unique.",
};

export default function DiscoveryFeed() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("public");
  const [stories, setStories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, has_next: false, has_previous: false });
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [followingIds, setFollowingIds] = useState(new Set());
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [selectedStory, setSelectedStory] = useState(null);

  // Load who the current user is following
  useEffect(() => {
    if (user && user.following_ids) {
      setFollowingIds(new Set(user.following_ids));
    }
  }, [user]);

  // Load bookmarked stories
  useEffect(() => {
    if (user) {
      axios.get(`${API}/saved`).then(res => {
        const ids = new Set(res.data.map(r => r.naid));
        setBookmarkedIds(ids);
      }).catch(() => {});
    }
  }, [user]);

  const fetchStories = useCallback(async (tab, page = 1, category = "") => {
    setLoading(true);
    try {
      let url;
      if (tab === "following") {
        url = `${API}/stories/following?page=${page}`;
      } else {
        url = `${API}/stories?page=${page}`;
        if (category) url += `&category=${encodeURIComponent(category)}`;
      }
      const res = await axios.get(url);
      setStories(res.data.stories);
      setPagination({
        page: res.data.page,
        total_pages: res.data.total_pages,
        has_next: res.data.has_next,
        has_previous: res.data.has_previous,
        total: res.data.total,
      });
    } catch {
      setStories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStories(activeTab, 1, categoryFilter);
  }, [activeTab, categoryFilter, fetchStories]);

  const handleFavorite = async (storyId) => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/stories/${storyId}/favorite`);
      setStories(prev => prev.map(s =>
        s.id === storyId ? { ...s, is_favorited: res.data.favorited, favorites_count: res.data.favorites_count } : s
      ));
    } catch (err) {
      console.error("[feed] favorite failed:", err?.response?.data?.detail || err?.message);
    }
  };

  const handleFollow = async (authorId) => {
    if (!user || authorId === user.id) return;
    try {
      if (followingIds.has(authorId)) {
        await axios.post(`${API}/unfollow/${authorId}`);
        setFollowingIds(prev => { const next = new Set(prev); next.delete(authorId); return next; });
      } else {
        await axios.post(`${API}/follow/${authorId}`);
        setFollowingIds(prev => new Set([...prev, authorId]));
      }
    } catch (err) {
      // 409/400 expected when already (un)followed; log others.
      const status = err?.response?.status;
      if (status !== 409 && status !== 400) {
        console.error("[feed] follow toggle failed:", status, err?.message);
      }
    }
  };

  const handleBookmark = async (story) => {
    if (!user) return;
    const storyNaid = `story_${story.id}`;
    if (bookmarkedIds.has(storyNaid)) {
      try {
        await axios.delete(`${API}/saved/${storyNaid}`);
        setBookmarkedIds(prev => { const next = new Set(prev); next.delete(storyNaid); return next; });
      } catch (err) {
        console.error("[feed] unbookmark failed:", err?.response?.data?.detail || err?.message);
      }
    } else {
      try {
        await axios.post(`${API}/saved`, {
          naid: storyNaid,
          title: story.title,
          description: story.content?.slice(0, 200),
          record_type: story.category,
          date_range: new Date(story.timestamp).toLocaleDateString(),
          has_online_content: false,
          raw_data: story,
        });
        setBookmarkedIds(prev => new Set([...prev, storyNaid]));
      } catch (err) {
        console.error("[feed] bookmark failed:", err?.response?.data?.detail || err?.message);
      }
    }
  };

  return (
    <div data-testid="discovery-feed">
      {/* Tabs */}
      <div className="flex items-center gap-4 mb-6 border-b border-[#E5E2D9] pb-3">
        <button
          className={`font-mono text-xs uppercase tracking-widest pb-2 border-b-2 transition-all ${activeTab === "public" ? "text-[#1A2B3C] border-[#B85C38]" : "text-[#718096] border-transparent hover:text-[#1A2B3C]"}`}
          onClick={() => setActiveTab("public")}
          data-testid="tab-public-feed"
        >
          <Users size={14} className="inline mr-1.5" />
          Public Feed
        </button>
        {user && (
          <button
            className={`font-mono text-xs uppercase tracking-widest pb-2 border-b-2 transition-all ${activeTab === "following" ? "text-[#1A2B3C] border-[#B85C38]" : "text-[#718096] border-transparent hover:text-[#1A2B3C]"}`}
            onClick={() => setActiveTab("following")}
            data-testid="tab-following-feed"
          >
            Following
          </button>
        )}
      </div>

      {/* Category Filters (public only) */}
      {activeTab === "public" && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            className={`filter-chip ${!categoryFilter ? "active" : ""}`}
            onClick={() => setCategoryFilter("")}
            data-testid="filter-category-all"
          >
            All
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`filter-chip ${categoryFilter === cat ? "active" : ""}`}
              onClick={() => setCategoryFilter(categoryFilter === cat ? "" : cat)}
              data-testid={`filter-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              title={CATEGORY_DESCRIPTIONS[cat]}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Stories */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="border border-[#E5E2D9] p-6 bg-white">
              <div className="skeleton h-5 w-20 mb-3" />
              <div className="skeleton h-6 w-3/4 mb-2" />
              <div className="skeleton h-4 w-full mb-2" />
              <div className="skeleton h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-feed">
          <PenLine size={40} className="mx-auto text-[#E5E2D9] mb-4" />
          <h3 className="font-serif text-xl text-[#718096] mb-2">
            {activeTab === "following" ? "No stories from people you follow yet" : "No stories yet"}
          </h3>
          <p className="text-sm text-[#718096]">
            {activeTab === "following" ? "Follow some Echoe Makers to see their stories here." : "Be the first Echoe Maker to share a story!"}
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="stories-list">
          {stories.map((story, i) => (
            <StoryCard
              key={story.id}
              story={story}
              index={i}
              onFavorite={handleFavorite}
              canFavorite={!!user}
              onFollow={handleFollow}
              isFollowing={followingIds.has(story.author_id)}
              isOwnStory={user && story.author_id === user.id}
              canFollow={!!user}
              onBookmark={handleBookmark}
              isBookmarked={bookmarkedIds.has(`story_${story.id}`)}
              canBookmark={!!user}
              onOpen={setSelectedStory}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E5E2D9]" data-testid="feed-pagination">
          <button
            className="btn-secondary"
            disabled={!pagination.has_previous}
            onClick={() => fetchStories(activeTab, pagination.page - 1, categoryFilter)}
            data-testid="feed-prev-page"
            style={{ opacity: pagination.has_previous ? 1 : 0.4 }}
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <span className="font-mono text-xs text-[#718096]">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            className="btn-secondary"
            disabled={!pagination.has_next}
            onClick={() => fetchStories(activeTab, pagination.page + 1, categoryFilter)}
            data-testid="feed-next-page"
            style={{ opacity: pagination.has_next ? 1 : 0.4 }}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Story Detail Modal */}
      {selectedStory && (
        <EchoeDetailModal
          story={selectedStory}
          onClose={() => setSelectedStory(null)}
          onFavorite={handleFavorite}
          canFavorite={!!user}
          onBookmark={handleBookmark}
          isBookmarked={bookmarkedIds.has(`story_${selectedStory.id}`)}
          canBookmark={!!user}
        />
      )}
    </div>
  );
}

function StoryCard({ story, index, onFavorite, canFavorite, onFollow, isFollowing, isOwnStory, canFollow, onBookmark, isBookmarked, canBookmark, onOpen }) {
  const catColor = CATEGORY_COLORS[story.category] || "#718096";

  return (
    <div
      className={`bg-white border border-[#E5E2D9] p-6 transition-all hover:shadow-sm animate-fade-in stagger-${(index % 8) + 1}`}
      data-testid={`story-card-${story.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span
            className="type-badge"
            style={{ borderColor: catColor, color: catColor }}
            data-testid={`story-category-${story.id}`}
          >
            {story.category}
          </span>
          {story.na_id && (
            <span className="type-badge">
              <MapPin size={10} />
              Sourced
            </span>
          )}
        </div>
        {/* Author name + Follow button */}
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-[#718096]">
            {story.author_name}
          </span>
          {canFollow && !isOwnStory && (
            <button
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-mono transition-all ${isFollowing ? "bg-[#2D6A4F] text-white border border-[#2D6A4F] shadow-sm" : "bg-[#1A2B3C] text-white border border-[#1A2B3C] hover:bg-[#2A405A]"}`}
              onClick={() => onFollow(story.author_id)}
              data-testid={`follow-btn-${story.author_id}`}
              title={isFollowing ? "Unfollow" : "Follow this Echoe Maker"}
            >
              {isFollowing ? <UserCheck size={12} /> : <UserPlus size={11} />}
              {isFollowing ? "Following" : "Follow"}
            </button>
          )}
        </div>
      </div>

      {/* Title & Content - clickable to open */}
      <div className="cursor-pointer" onClick={() => onOpen(story)}>
        <h3 className="font-serif text-lg font-medium text-[#1A1A1A] mb-2 hover:text-[#B85C38] transition-colors" data-testid={`story-title-${story.id}`}>
          {story.title}
        </h3>
        <p className="text-sm text-[#4A5568] leading-relaxed mb-2 line-clamp-3">
          {story.content}
        </p>
        {story.content && story.content.length > 200 && (
          <span className="text-xs text-[#B85C38] font-medium hover:underline" data-testid={`read-more-${story.id}`}>
            Read full echoe...
          </span>
        )}
      </div>

      {/* Image */}
      {story.original_image_url && (
        <div className="mt-3 mb-3 border border-[#E5E2D9] overflow-hidden cursor-pointer" onClick={() => onOpen(story)}>
          <img
            src={story.original_image_url}
            alt={story.title}
            className="w-full h-48 object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-[#E5E2D9] mt-3">
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#4A5568] font-medium" data-testid={`story-author-${story.id}`}>
            {story.author_role === "echo_maker" ? "Echoe Maker" : "Echoe Hunter"}
          </span>
          <span className="font-mono text-xs text-[#718096]">
            <Clock size={11} className="inline mr-1" />
            {new Date(story.timestamp).toLocaleDateString()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Bookmark button */}
          {canBookmark && (
            <button
              className={`btn-ghost p-1.5 ${isBookmarked ? "text-[#B85C38]" : ""}`}
              onClick={() => onBookmark(story)}
              data-testid={`bookmark-btn-${story.id}`}
              title={isBookmarked ? "Remove bookmark" : "Bookmark this echoe"}
            >
              {isBookmarked ? <BookmarkCheck size={16} fill="#B85C38" /> : <Bookmark size={16} />}
            </button>
          )}

          {/* Favorite button */}
          <button
            className={`btn-ghost flex items-center gap-1.5 ${story.is_favorited ? "text-[#B85C38]" : ""}`}
            onClick={() => canFavorite && onFavorite(story.id)}
            disabled={!canFavorite}
            data-testid={`favorite-btn-${story.id}`}
            title={canFavorite ? (story.is_favorited ? "Unfavorite" : "Favorite") : "Sign in to favorite"}
          >
            <Heart size={16} fill={story.is_favorited ? "#B85C38" : "none"} />
            <span className="text-xs font-mono">{story.favorites_count}</span>
          </button>
        </div>
      </div>

      {/* Source Link */}
      {story.na_id && (
        <div className="mt-3 pt-3 border-t border-[#E5E2D9]">
          <a
            href={story.na_id.startsWith("http") ? story.na_id : `https://catalog.archives.gov/id/${story.na_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-[#1A2B3C] text-white font-mono text-xs hover:bg-[#2A405A] transition-colors"
            data-testid={`story-nara-link-${story.id}`}
          >
            <ExternalLink size={12} />
            View on National Archives
          </a>
        </div>
      )}
    </div>
  );
}
